/**
 * Fire on the game side (GDD sections 6, 8 and 12): chain explosions of black powder queued by the simulation,
 * Barbe Noire's lit beard, and the flamethrower jet.
 */
import { Physique, STATE_LIQUID, isqrt, nextInt, cosDeg, sinDeg, wakeRect, TRIG_ONE, FLAG_FLAMMABLE } from '@invaders/sim';
import { FP_SHIFT, TICKS_PER_SECOND, type GameState, type Unit } from './state';
import { BARBE_NOIRE, INVADERS } from './invaders';
import { UNIT_H, UNIT_W, damageUnit, spared, touches, unitCenter } from './unit';
import { explode } from './explosion';

const { EMPTY, FIRE, LAVA, WATER, STEAM, ICE } = Physique;

/**
 * Damage of a powder explosion of the base radius, what each merged cell adds, and the most it can do: 25, 2 and 60
 * tripled (author's choice: +200 %). Not in the GDD: tuning values.
 */
export const POWDER_DAMAGE = 75;
export const POWDER_DAMAGE_PER_CELL = 6;
export const POWDER_DAMAGE_MAX = 180;
/** Powder blasts closer than this (in cells) in the same tick make one bigger explosion. */
export const BLAST_MERGE = 8;
export const MAX_POWDER_RADIUS = 20;

/**
 * Turns the explosions queued by the simulation (black powder set off by fire or lava, ice touching lava) into real
 * ones. A heap
 * of powder fires many cells at once: the close ones merge into one explosion that grows with their number.
 * Its rim sets the rest of the heap on fire, so a heap blows up in a chain over a few ticks.
 */
export function processBlasts(state: GameState): void {
  const q = state.world.blasts;
  if (q.length === 0) return;
  const clusters: { x: number; y: number; n: number; r: number }[] = [];
  for (let i = 0; i < q.length; i += 3) {
    const x = q[i];
    const y = q[i + 1];
    const c = clusters.find((k) => Math.abs(Math.trunc(k.x / k.n) - x) <= BLAST_MERGE && Math.abs(Math.trunc(k.y / k.n) - y) <= BLAST_MERGE);
    if (c) { c.x += x; c.y += y; c.n++; c.r = Math.max(c.r, q[i + 2]); } else clusters.push({ x, y, n: 1, r: q[i + 2] });
  }
  q.length = 0;
  for (const c of clusters) {
    const radius = Math.min(MAX_POWDER_RADIUS, c.r + isqrt(c.n * 4));
    explode(state, Math.trunc(c.x / c.n), Math.trunc(c.y / c.n), radius, Math.min(POWDER_DAMAGE_MAX, POWDER_DAMAGE + POWDER_DAMAGE_PER_CELL * (c.n - 1)));
  }
}

// --- Barbe Noire ----------------------------------------------------------------------------------

/** GDD: lit, his beard blows up after 2 s (40 damage). */
export const BEARD_FUSE_TICKS = 2 * TICKS_PER_SECOND;
export const BEARD_DAMAGE = 40;
export const BEARD_RADIUS = 16;

/** Wet, Barbe Noire risks nothing any more; on fire or in lava, his beard starts burning down. */
export function updateFuses(state: GameState): void {
  const world = state.world;
  for (const u of state.units) {
    if (!u.alive || u.kind !== BARBE_NOIRE || u.wet) continue;
    if (touches(world, u, WATER)) { u.wet = true; u.fuse = 0; continue; }
    if (u.fuse === 0 && (touches(world, u, FIRE) || touches(world, u, LAVA))) u.fuse = BEARD_FUSE_TICKS;
    else if (u.fuse > 0) {
      u.fuse--;
      if (u.fuse === 0) {
        const [cx, cy] = unitCenter(u);
        u.wet = true; // the powder of his beard is spent
        explode(state, cx, cy, BEARD_RADIUS, BEARD_DAMAGE);
      }
    }
  }
}

// --- flamethrower ---------------------------------------------------------------------------------

/** GDD: the jet reaches about 105 cells, and its cone widens to 44 cells at the end. */
export const FLAME_RANGE = 105;
export const FLAME_END_WIDTH = 44;
/** GDD: 1.5 s at most while the button is held. */
export const FLAME_TICKS = 3 * TICKS_PER_SECOND / 2;
/** Flame tongues thrown per tick. */
const FLAME_SAMPLES = 10;
/**
 * The jet burns the units it licks while it is used (author's rule; the GDD only has the fire's contact damage at the
 * resolution): every FLAME_HIT_EVERY ticks, FLAME_HIT_DAMAGE of fire to each unit a tongue goes through (its
 * thrower aside), which catches fire (a burn at the next resolution). At most 45 for a whole jet. Values chosen.
 */
export const FLAME_HIT_EVERY = 10;
export const FLAME_HIT_DAMAGE = 5;

export function startJet(state: GameState, unit: Unit, angle: number): void {
  state.jets = state.jets.filter((j) => j.unit !== unit.id);
  state.jets.push({ unit: unit.id, angle, ticks: FLAME_TICKS });
}

export function aimJet(state: GameState, unit: number, angle: number): void {
  const j = state.jets.find((k) => k.unit === unit);
  if (j) j.angle = angle;
}

export function stopJet(state: GameState, unit: number): void {
  state.jets = state.jets.filter((j) => j.unit !== unit);
}

/**
 * Each tick the jet throws tongues of fire at random points of its cone. A tongue stops on the first solid:
 * it sets what burns on fire (wood, oil, black powder, which then blows up), melts the ice it hits into water
 * (author's addition: the ice melts faster under the flame), and leaves a flame against the rest. In water it goes
 * out into steam. It burns the units its tongues go through (FLAME_HIT_DAMAGE).
 */
export function updateJets(state: GameState): void {
  const world = state.world;
  const { w, h, data } = world.physique;
  const cells = world.cellState.data;
  const tables = world.rules.materials;
  const rng = world.rng;
  for (let k = state.jets.length - 1; k >= 0; k--) {
    const j = state.jets[k];
    const u = state.units[j.unit];
    if (!u || !u.alive || j.ticks <= 0) { state.jets.splice(k, 1); continue; }
    j.ticks--;
    const [cx, cy] = unitCenter(u);
    u.facing = cosDeg(j.angle) < 0 ? -1 : 1;
    const cos = cosDeg(j.angle);
    const sin = sinDeg(j.angle);
    let minX = cx;
    let maxX = cx;
    let minY = cy;
    let maxY = cy;
    const burns = j.ticks % FLAME_HIT_EVERY === 0;
    let licked = 0;
    for (let n = 0; n < FLAME_SAMPLES; n++) {
      const dist = 8 + nextInt(rng, FLAME_RANGE - 7);
      const half = Math.trunc((dist * (FLAME_END_WIDTH >> 1)) / FLAME_RANGE);
      const side = nextInt(rng, 2 * half + 1) - half;
      // End point: along the aim, then sideways (perpendicular).
      const ex = cx + Math.trunc((dist * cos - side * sin) / TRIG_ONE);
      const ey = cy - Math.trunc((dist * sin + side * cos) / TRIG_ONE);
      const steps = Math.max(Math.abs(ex - cx), Math.abs(ey - cy));
      let px = cx;
      let py = cy;
      for (let s = 1; s <= steps; s++) {
        const x = cx + Math.trunc(((ex - cx) * s) / steps);
        const y = cy + Math.trunc(((ey - cy) * s) / steps);
        if (x < 0 || x >= w || y < 0 || y >= h) break;
        const i = y * w + x;
        const m = data[i];
        if (m === WATER) { data[i] = STEAM; cells[i] = 0; px = -1; break; }
        if (m === ICE) { data[i] = WATER; cells[i] = 0; px = -1; break; }
        if (tables.state[m] < STATE_LIQUID || (tables.flags[m] & FLAG_FLAMMABLE) !== 0) {
          if ((tables.flags[m] & FLAG_FLAMMABLE) !== 0) { data[i] = FIRE; cells[i] = 0; px = -1; }
          break;
        }
        if (burns) licked |= lickedUnits(state, x, y, u.id);
        px = x;
        py = y;
      }
      if (px >= 0 && (px !== cx || py !== cy) && !insideUnit(state, px, py)) {
        const i = py * w + px;
        if (data[i] === EMPTY || tables.state[data[i]] > STATE_LIQUID) { data[i] = FIRE; cells[i] = 0; }
      }
      if (ex < minX) minX = ex;
      if (ex > maxX) maxX = ex;
      if (ey < minY) minY = ey;
      if (ey > maxY) maxY = ey;
    }
    wakeRect(world, minX - 1, minY - 1, maxX + 1, maxY + 1);
    if (licked !== 0) burnUnits(state, u, licked);
  }
}

/** Bits (by unit id, the first 31) of the living units other than `ignore` whose body covers cell (x, y). */
function lickedUnits(state: GameState, x: number, y: number, ignore: number): number {
  let bits = 0;
  for (const u of state.units) {
    if (!u.alive || u.id === ignore || u.id > 30) continue;
    const ux = u.x >> FP_SHIFT;
    const uy = u.y >> FP_SHIFT;
    if (x >= ux && x < ux + UNIT_W && y >= uy && y < uy + UNIT_H) bits |= 1 << u.id;
  }
  return bits;
}

/** The units the jet of `thrower` licked: fire damage, and they catch fire (friendly fire: its team is spared). */
function burnUnits(state: GameState, thrower: Unit, licked: number): void {
  state.source = thrower.player;
  for (const t of state.units) {
    if (((licked >> t.id) & 1) === 0 || t.id > 30 || !t.alive || spared(state, t)) continue;
    damageUnit(state, t, FLAME_HIT_DAMAGE, 'fire');
    if (t.alive && INVADERS[t.kind].fire > 0) t.burning = true;
  }
  state.source = -1;
}

function insideUnit(state: GameState, x: number, y: number): boolean {
  for (const u of state.units) {
    if (!u.alive) continue;
    const ux = u.x >> FP_SHIFT;
    const uy = u.y >> FP_SHIFT;
    if (x >= ux && x < ux + UNIT_W && y >= uy && y < uy + UNIT_H) return true;
  }
  return false;
}
